import useDeleteConfirmDialog from 'common/components/useDeleteConfirmDialog'
import { CRUDAction, FeatureKey } from 'common/constants/permission'
import { LoginBody } from 'modules/auth/api/types'
import { useMutateDeleteMembers, useMutateUpdateMembers } from 'modules/member/api/mutation'
import { useRoles } from 'modules/member/api/selector'
import { MemberResponse, MemberScopeFarm } from 'modules/member/api/types'
import CreateMemberDialog from 'modules/member/components/CreateMemberDialog'
import { RoleName } from 'modules/member/constants'
import { transformFarmsData } from 'modules/member/utils'
import MoreAction from 'modules/pond/components/MoreAction'
import { useUserInfo } from 'modules/user/api/queries'
import { usePermitToActionFeature } from 'modules/user/api/selector'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'

import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Button,
  Card,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

import Page from 'components/Page'


interface MemberPageProps {
  members: MemberResponse[]
  isMemberFetching: boolean
  refetchMembers: () => void
}

const MemberPage = ({ members, isMemberFetching, refetchMembers }: MemberPageProps) => {
  const location = useLocation()
  const { t } = useTranslation(['member', 'pond'])
  const [openMemberDialog, setOpenMemberDialog] = useState(false)
  const [isEditMemberDialog, setIsEditDialog] = useState(false)
  const [selectedMember, setSelectedMember] = useState<MemberResponse | null>(null)
  const haveEditPermission = usePermitToActionFeature([CRUDAction.Update], FeatureKey.User)
  const [activeTab, setActiveTab] = useState(0)
  const [openSelect, setOpenSelect] = useState<string | null>(null)
  const [isRoleLoading, setRoleLoading] = useState(false)

  useEffect(() => {
    setActiveTab(location.pathname.endsWith('/roles') ? 1 : 0)
  }, [location.pathname])

  const handleOpen = () => {
    setOpenMemberDialog(true)
  }

  const handleClose = (isOpenDetail?: boolean, data?: LoginBody) => {
    setIsEditDialog(false)
    setSelectedMember(
      isOpenDetail
        ? {
            id: '',
            phone: data?.username,
            organization_id: data?.organization_id,
            password: data?.password,
          }
        : null
    )
    setOpenMemberDialog(false)
  }

  const { data: roles, isFetching: isRoleFetching } = useRoles()
  const { data: userInfo } = useUserInfo()

  const updateMembersMutation = useMutateUpdateMembers()
  const deleteMemberMutation = useMutateDeleteMembers()

  const secondaryOwnerRole = roles?.find((r) => r.name === RoleName.SecondaryOwner)
  const roleWithoutPrimaryOwners = roles?.filter((r) => r.name !== RoleName.PrimaryOwner)

  const setNewRole = async (
    memberId: string,
    userRoleId: string,
    name: string,
    phone: string,
    scope: MemberScopeFarm[]
  ) => {
    if (!userRoleId || !userInfo || !memberId) return

    try {
      setRoleLoading(true)
      await updateMembersMutation.mutateAsync({
        userId: userInfo.id,
        memberId,
        body: {
          name,
          phone,
          user_role_id: userRoleId,
          scopes: transformFarmsData(scope),
        },
      })
    } catch (error) {
      console.error('Failed to update member role:', error)
    } finally {
      setRoleLoading(false)
    }
  }

  const onClickEditBtn = (member: MemberResponse) => {
    setIsEditDialog(true)
    setSelectedMember(member)
    handleOpen()
  }

  const formatPhone = (value?: string) => {
    if (!value) return '-'
    const digits = value.replace(/\D/g, '')

    return digits.replace(/(\d{3})(\d{4})(\d{0,3})/, (_, a, b, c) =>
      c ? `${a}-${b}-${c}` : `${a}-${b}`
    )
  }

  const createTableRows = (member: MemberResponse) => {
    const {
      id,
      name = '',
      user_role_id = '',
      is_owner = false,
      member_scopes: scopes = null,
      phone = '',
    } = member

    const isSecondaryOwner = !!secondaryOwnerRole && user_role_id === secondaryOwnerRole.id
    const isPrimaryOwner = is_owner && !isSecondaryOwner
    const showAllFarmsPonds = !scopes && is_owner

    return {
      name: <Typography>{name}</Typography>,
      phone: <Typography>{formatPhone(phone)}</Typography>,
      role: !isRoleFetching ? (
        isPrimaryOwner ? (
          <Typography color="primary">{RoleName.PrimaryOwner}</Typography>
        ) : (
          <FormControl
            fullWidth
            variant="standard"
            sx={{
              '& .MuiInputBase-root': {
                '&:before, &:after': { display: 'none' },
              },
            }}
          >
            <Select
              className="member-select"
              labelId="role-select-label"
              open={openSelect === id}
              onOpen={() => setOpenSelect(id)}
              onClose={() => setOpenSelect(null)}
              value={
                roleWithoutPrimaryOwners?.some((r) => r.id === user_role_id) ? user_role_id : '-'
              }
              onChange={(e) => setNewRole(id, e.target.value, name, phone, scopes?.farms ?? [])}
              displayEmpty
              renderValue={(value) => {
                const selectedRoleName =
                  value === '-' ? '-' : roleWithoutPrimaryOwners?.find((r) => r.id === value)?.name
                return (
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    {selectedRoleName}
                    {openSelect === id ? (
                      <ExpandLessIcon className="expand-icon" />
                    ) : (
                      <ExpandMoreIcon className="expand-icon" />
                    )}
                  </Stack>
                )
              }}
              MenuProps={{ PaperProps: { style: { maxHeight: '220px' } } }}
              disabled={isRoleLoading || !haveEditPermission}
            >
              {roleWithoutPrimaryOwners?.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.name.trim() || '-'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )
      ) : (
        <Box />
      ),
      farms: (
        <Stack spacing={1}>
          {showAllFarmsPonds ? (
            <Typography>{t('member:all.farm')}</Typography>
          ) : scopes?.farms ? (
            scopes.farms.map((farm, index) => (
              <Typography key={farm.id ?? index}>
                {t('member:table.farm')} {farm.name}
              </Typography>
            ))
          ) : (
            <Button
              color="error"
              variant="text"
              onClick={() => onClickEditBtn(member)}
              className="set-value-button"
            >
              {t('member:please.choose.farm')}
            </Button>
          )}
        </Stack>
      ),

      pond: (
        <Stack spacing={1}>
          {showAllFarmsPonds ? (
            <Typography>{t('member:all.pond')}</Typography>
          ) : scopes?.farms ? (
            scopes.farms.map((farm, index) => (
              <Typography key={farm.id ?? index}>
                {farm.ponds?.map((p) => `${t('member:table.pond')} ${p.name || '-'}`).join(', ')}
                {/* <Box sx={{ opacity: 0 }}>
                  {t('member:table.farm')} {farm.name}
                </Box> */}
              </Typography>
            ))
          ) : (
            <Button
              color="error"
              variant="text"
              onClick={() => onClickEditBtn(member)}
              className="set-value-button"
            >
              {t('member:please.choose.pond')}
            </Button>
          )}
        </Stack>
      ),
      action: !isPrimaryOwner && (
        <MoreAction
          featureKey={FeatureKey.User}
          onClickOptions={[
            {
              icon: <EditIcon />,
              text: t('pond:edit'),
              action: () => onClickEditBtn(member),
              crudIdx: CRUDAction.Update,
            },
            {
              icon: <DeleteIcon />,
              text: t('pond:delete'),
              action: () => {
                setSelectedMember(member)
                openConfirmDeleteDialog()
              },
              crudIdx: CRUDAction.Delete,
            },
          ]}
        />
      ),
    }
  }

  const rows = members.map(createTableRows)

  const columns = [
    { id: 'name', title: t('table.name'), width: '150px' },
    { id: 'phone', title: t('table.phone'), width: '150px' },
    { id: 'role', title: t('table.role'), width: '150px' },
    { id: 'farms', title: t('table.farm'), width: '150px' },
    { id: 'pond', title: t('table.pond'), width: '150px' },
    { id: 'action', title: '', width: '80px' },
  ]

  const { dialog, openDialog: openConfirmDeleteDialog } = useDeleteConfirmDialog({
    onConfirm: async () => {
      if (!userInfo || !selectedMember?.id) return

      await deleteMemberMutation.mutateAsync({
        userId: userInfo.id,
        memberId: selectedMember.id,
      })
    },
  })

  return (
    <Page title={activeTab === 0 ? t('staff.list') : t('manage.role')}>
      {dialog}
      <Card
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          height: '70vh',
          bgcolor: 'common.white',
          borderRadius: '24px',
          p: 5,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            color: 'common.black',
            fontWeight: 600,
            fontSize: '18px',
            mb: 2,
          }}
        >
          {t('member:staff.all.list')}
        </Typography>

        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{
              borderRadius: 2,
              width: '100%',
              overflowX: 'auto',
            }}
          >
            <Table
              stickyHeader
              aria-label="member table"
              sx={{
                tableLayout: 'fixed',
                borderCollapse: 'separate',
                '& thead th': {
                  fontSize: '16px',
                  color: 'text.primary',
                  backgroundColor: '#F0F0F0',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  height: 54,
                  textAlign: 'left',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                },
                '& tbody td': {
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  overflow: 'visible',
                  padding: '16px',
                  verticalAlign: 'top',
                },
                '& thead th:first-of-type': { borderTopLeftRadius: 16 },
                '& thead th:last-of-type': { borderTopRightRadius: 16 },
                '& tbody tr:last-of-type td:first-of-type': { borderBottomLeftRadius: 16 },
                '& tbody tr:last-of-type td:last-of-type': { borderBottomRightRadius: 16 },
              }}
            >
              <TableHead>
                <TableRow>
                  {columns.map((col) => (
                    <TableCell
                      key={col.id}
                      sx={{
                        fontWeight: 600,
                        width: col.width,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {col.title}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      align="center"
                      sx={{ py: 5, fontSize: '16px', color: 'text.secondary' }}
                    >
                      {t('common:no.data')}
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row, index) => (
                    <TableRow key={index} hover>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{row.phone}</TableCell>
                      <TableCell>{row.role}</TableCell>
                      <TableCell>{row.farms}</TableCell>
                      <TableCell>{row.pond}</TableCell>
                      <TableCell align="right">{row.action}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Card>

      {openMemberDialog && (
        <CreateMemberDialog
          open={openMemberDialog}
          onClose={handleClose}
          isEdit={isEditMemberDialog}
          initialMember={selectedMember}
          onSuccess={() => refetchMembers()}
        />
      )}
    </Page>
  )
}

export default MemberPage
